import { useCallback, useState } from 'preact/hooks';
import { t } from '../lib/i18n';
import type { AttachmentsConfig, MediaKind } from '../client/api-types';
import type { UploadedAttachment } from '../client/attachments';

/**
 * Owns the file a visitor is about to send: upload state, errors, and the
 * drag-and-drop wiring.
 *
 * It lives here rather than inside the composer because **the whole chat is
 * the drop target**, not just the input row. Dropping a file on the message
 * list is the natural gesture, and that means the panel needs the handlers
 * while the composer needs the resulting state — one owner, two consumers.
 */

export interface PendingAttachment {
  name: string;
  /** Derived from the file at pick time so the icon shows during the upload. */
  kind: MediaKind;
  size: number;
  uploading: boolean;
  uploaded?: UploadedAttachment;
  error?: string;
}

interface Options {
  attachments?: AttachmentsConfig;
  onUpload?: (file: File) => Promise<UploadedAttachment>;
}

/** Maps the server's refusal code to something a visitor can act on. */
function errorMessage(code: string | undefined, maxFileMb: number): string {
  switch (code) {
    case 'file_too_large':
      return t('attach_error_too_large', { max: String(maxFileMb) });
    case 'mime_type_not_allowed':
      return t('attach_error_type');
    case 'attachments_not_enabled':
      return t('attach_error_disabled');
    case 'rate_limit_exceeded':
      return t('attach_error_rate');
    default:
      return t('attach_error_generic');
  }
}

export function useComposerAttachment({ attachments, onUpload }: Options) {
  const [pending, setPending] = useState<PendingAttachment | null>(null);
  const [dragging, setDragging] = useState(false);

  const enabled = !!attachments && !!onUpload;

  const clearAttachment = useCallback(() => setPending(null), []);

  /**
   * Single entry point for every way a file can arrive — picker, drop or
   * paste. Uploading happens here rather than on send: a refusal shows up
   * while the visitor is still looking at the file they chose, not after
   * they wrote a caption and hit send.
   */
  const acceptFile = useCallback(
    async (file: File) => {
      if (!enabled || !onUpload) return;

      const kind: MediaKind = file.type.startsWith('image/')
        ? 'image'
        : 'document';
      const base = { name: file.name, kind, size: file.size };

      setPending({ ...base, uploading: true });
      try {
        const uploaded = await onUpload(file);
        setPending({ ...base, uploading: false, uploaded });
      } catch (err) {
        setPending({
          ...base,
          uploading: false,
          error: errorMessage(
            (err as { code?: string }).code,
            attachments?.maxFileMb ?? 0,
          ),
        });
      }
    },
    [enabled, onUpload, attachments?.maxFileMb],
  );

  /** A drag only counts when it actually carries files. */
  const dragHasFiles = (e: DragEvent): boolean =>
    Array.from(e.dataTransfer?.types ?? []).includes('Files');

  const dropHandlers = {
    onDragOver: (e: DragEvent) => {
      if (!enabled || !dragHasFiles(e)) return;
      // Without preventDefault the browser navigates to the dropped file and
      // the visitor loses the conversation.
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: (e: DragEvent) => {
      // `dragleave` bubbles from every child, so moving the file from the
      // transcript to the composer fires one even though the pointer never
      // left the panel. Only a leave whose destination is outside the panel
      // — or nowhere, when it exits the window — actually ends the drag.
      const to = e.relatedTarget as Node | null;
      if (to && (e.currentTarget as Node | null)?.contains(to)) return;
      setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      setDragging(false);
      if (!enabled || !dragHasFiles(e)) return;
      e.preventDefault();

      const file = e.dataTransfer?.files?.[0];
      if (file) void acceptFile(file);
    },
  };

  return { pending, dragging, acceptFile, clearAttachment, dropHandlers };
}
